import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { API_BASE_URL } from '../../config.js';
import SortableTable from '../SortableTable';
import StatCard from '../ui/StatCard';
import StatTileNew from '../ui/StatTileNew';
import Badge from '../ui/Badge';
import InfoTip from '../ui/InfoTip';
import { Modal } from '../ui/Modal';
import VisitsQualityVisitContent from './VisitsQualityVisitContent';
import { obfuscateUsername } from '../../utils/obfuscateUsername';
import { sourceParam } from '../../constants/visitsSource';

/**
 * "Contrôle qualité" — confronts the time MEASURED by the app (its read-only
 * 'Form duration' stopwatch) against the time DECLARED by the phone.
 *
 * Two rules govern this screen, and they are not decoration:
 *  1. An indicator that could not be computed shows "n/c", never 0. A project
 *     without a stopwatch, or without a declared duration, must not read as
 *     "no anomaly found".
 *  2. Arithmetic proof and mere hints are visually separated. Coverage (signal 2)
 *     is a prioritisation aid: the stopwatch only measures time spent inside the
 *     form blocks, not merchandising, not talking to staff, not walking the store.
 *
 * Vocabulary stays neutral throughout: "à vérifier", "incohérence détectée".
 */

const ymd = (d) => (d instanceof Date ? d.toISOString().split('T')[0] : String(d || ''));
const fmtPct = (v, digits = 1) => (v === null || v === undefined ? null : `${(v * 100).toFixed(digits)} %`);
const fmtMin = (v) => {
  if (v === null || v === undefined) return null;
  const abs = Math.abs(v);
  if (abs < 90) return `${Math.round(v)} min`;
  if (abs < 60 * 24) return `${v < 0 ? '-' : ''}${Math.floor(abs / 60)} h ${String(Math.round(abs % 60)).padStart(2, '0')}`;
  return `${v < 0 ? '-' : ''}${(abs / 1440).toFixed(1)} j`;
};

// The single rendering rule of this screen: nothing measurable → "n/c", not 0.
const NC = <span className="text-gray-400 dark:text-gray-500" title="Non calculable sur ce projet / cette période">n/c</span>;
const orNC = (v, render = (x) => x) => (v === null || v === undefined ? NC : render(v));

const AVAILABILITY_LABEL = {
  available: { tone: 'green', text: 'disponible' },
  partial: { tone: 'amber', text: 'partielle' },
  unavailable: { tone: 'slate', text: 'indisponible' },
};

const RELIABILITY = {
  reliable: { tone: 'green', text: 'fiable', tip: "Cet intervenant synchronise rapidement : si son horloge était décalée, le test le verrait." },
  inconclusive: {
    tone: 'amber',
    text: 'non concluant',
    tip: "Cet intervenant synchronise longtemps après ses visites. Le test devient aveugle : une horloge avancée ne produit plus de contradiction. Absence de cas détecté ne vaut donc pas absence de problème.",
  },
  'project-suspect': {
    tone: 'slate',
    text: 'à valider',
    tip: "Sur ce projet, le pire délai se compte en jours : ce n'est pas un décalage d'horloge mais très probablement un autre mode de saisie. À valider avant d'exploiter le signal.",
  },
};

const Banner = ({ tone = 'amber', title, children }) => {
  const tones = {
    amber: 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100',
    slate: 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-200',
  };
  return (
    <div className={`rounded-xl border p-3.5 text-sm leading-snug text-left ${tones[tone] || tones.amber}`}>
      <p className="font-semibold">{title}</p>
      <p className="mt-0.5 opacity-90">{children}</p>
    </div>
  );
};

const useQualityData = ({ selectedProject, startDate, endDate, fetchWithAuth }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    const projectId = selectedProject?.value;
    if (!projectId || !startDate || !endDate) {
      setState({ loading: false, error: null, data: null });
      return undefined;
    }

    const load = async () => {
      setState({ loading: true, error: null, data: null });
      try {
        const res = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/visits/${projectId}/quality`
          + `?${sourceParam(selectedProject)}&startDate=${ymd(startDate)}&endDate=${ymd(endDate)}`
        );
        if (!res.ok) throw new Error(`Le serveur a répondu ${res.status}`);
        const data = await res.json();
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        // Surfaced, never swallowed: on a control screen an empty table would be
        // read as "nothing to report".
        if (!cancelled) setState({ loading: false, error: e.message || 'Erreur inconnue', data: null });
      }
    };

    load();
    return () => { cancelled = true; };
  }, [selectedProject, startDate, endDate, fetchWithAuth]);

  return state;
};

const VisitsQuality = ({ selectedProject, startDate, endDate, fetchWithAuth, obfuscate }) => {
  const { loading, error, data } = useQualityData({ selectedProject, startDate, endDate, fetchWithAuth });
  const [selectedUser, setSelectedUser] = useState(null);
  const [detail, setDetail] = useState({ loading: false, error: null, visits: [] });
  const [visitContent, setVisitContent] = useState({ visitId: null, loading: false, error: null, data: null });

  const project = data?.project;
  const users = data?.users || [];

  const displayName = useCallback(
    (u) => (obfuscate ? obfuscateUsername(u.userName) : u.userName),
    [obfuscate]
  );

  const closeDrilldown = useCallback(() => {
    setSelectedUser(null);
    setVisitContent({ visitId: null, loading: false, error: null, data: null });
  }, []);

  useEffect(() => { closeDrilldown(); }, [selectedProject, startDate, endDate, closeDrilldown]);

  const openDetail = useCallback(async (user) => {
    setSelectedUser(user);
    setVisitContent({ visitId: null, loading: false, error: null, data: null });
    setDetail({ loading: true, error: null, visits: [] });
    try {
      const res = await fetchWithAuth(
        `${API_BASE_URL}/api/dwh/visits/${selectedProject.value}/quality/${user.userId}`
        + `?${sourceParam(selectedProject)}&startDate=${ymd(startDate)}&endDate=${ymd(endDate)}`
      );
      if (!res.ok) throw new Error(`Le serveur a répondu ${res.status}`);
      const body = await res.json();
      setDetail({ loading: false, error: null, visits: body.visits || [] });
    } catch (e) {
      setDetail({ loading: false, error: e.message || 'Erreur inconnue', visits: [] });
    }
  }, [fetchWithAuth, selectedProject, startDate, endDate]);

  const openVisit = useCallback(async (visit) => {
    setVisitContent({ visitId: visit.visitId, loading: true, error: null, data: null });
    try {
      const res = await fetchWithAuth(
        `${API_BASE_URL}/api/dwh/visits/${selectedProject.value}/quality/visit/${encodeURIComponent(visit.visitId)}`
        + `?${sourceParam(selectedProject)}&startDate=${ymd(startDate)}&endDate=${ymd(endDate)}`
      );
      if (!res.ok) throw new Error(`Le serveur a répondu ${res.status}`);
      const data = await res.json();
      setVisitContent({ visitId: visit.visitId, loading: false, error: null, data });
    } catch (e) {
      setVisitContent({ visitId: visit.visitId, loading: false, error: e.message || 'Erreur inconnue', data: null });
    }
  }, [fetchWithAuth, selectedProject, startDate, endDate]);

  const totals = useMemo(() => {
    const sum = (key) => users.reduce((acc, u) => (u[key] === null ? acc : acc + u[key]), 0);
    const anyComputable = (key) => users.some((u) => u[key] !== null);
    return {
      signal1: anyComputable('signal1Count') ? sum('signal1Count') : null,
      signal3: anyComputable('signal3Count') ? sum('signal3Count') : null,
      flaggedPeople: users.filter((u) => (u.signal1Count || 0) > 0).length,
    };
  }, [users]);

  const hasChrono = project?.chronoAvailability !== 'unavailable';
  const hasDelay = project?.signal3Availability !== 'unavailable';

  // Proof columns carry a tint; hint columns stay neutral. The distinction is the
  // point: one is a contradiction, the other is a reason to go and look.
  const proofHead = 'bg-indigo-50/70 dark:bg-indigo-900/20';
  const proofCell = 'bg-indigo-50/40 dark:bg-indigo-900/10 font-semibold';

  const columns = useMemo(() => [
    { key: 'userName', label: 'Intervenant', render: (u) => displayName(u), rawValue: (u) => displayName(u) },
    { key: 'nbVisits', label: 'Visites', render: (u) => u.nbVisits },
    {
      key: 'signal1Count',
      exportLabel: 'Incohérences dures',
      label: (
        <span className="inline-flex items-center gap-1">
          Incohérences dures
          <InfoTip width="w-72" text="Le temps mesuré par le chronomètre de l'application dépasse la durée déclarée. C'est une contradiction interne : la durée déclarée est nécessairement fausse. Aucun faux positif possible. Limite : ne se calcule que si le projet utilise un chronomètre de formulaire." />
        </span>
      ),
      headerClassName: proofHead,
      cellClassName: proofCell,
      render: (u) => orNC(u.signal1Count, (n) => (
        n > 0 ? <span className="text-red-600 dark:text-red-400">{n}</span> : <span className="text-gray-500">0</span>
      )),
      rawValue: (u) => (u.signal1Count === null ? '' : u.signal1Count),
    },
    {
      key: 'signal3Count',
      exportLabel: 'Délais impossibles',
      label: (
        <span className="inline-flex items-center gap-1">
          Délais impossibles
          <InfoTip width="w-72" text="Les réponses sont parvenues au serveur avant la fin déclarée de la visite. L'horodatage serveur ne dépend pas du téléphone : c'est une preuve d'horloge décalée. Limite majeure : le test est aveugle si l'intervenant synchronise longtemps après sa visite — voir la colonne Fiabilité." />
        </span>
      ),
      headerClassName: proofHead,
      cellClassName: proofCell,
      render: (u) => orNC(u.signal3Count, (n) => (
        n > 0 ? <span className="text-red-600 dark:text-red-400">{n}</span> : <span className="text-gray-500">0</span>
      )),
      rawValue: (u) => (u.signal3Count === null ? '' : u.signal3Count),
    },
    {
      key: 'signal3Reliability',
      exportLabel: 'Fiabilité indicateur 3',
      label: (
        <span className="inline-flex items-center gap-1">
          Fiabilité
          <InfoTip width="w-72" text="Ce que vaut la colonne « Délais impossibles » pour cet intervenant. « Non concluant » ne veut pas dire « rien à signaler » : le test n'a simplement rien pu voir." />
        </span>
      ),
      headerClassName: proofHead,
      cellClassName: proofCell,
      render: (u) => {
        const r = RELIABILITY[u.signal3Reliability] || RELIABILITY.inconclusive;
        return (
          <span className="inline-flex items-center gap-1">
            <Badge tone={r.tone} dot>{r.text}</Badge>
            <InfoTip width="w-72" text={r.tip} />
          </span>
        );
      },
      rawValue: (u) => u.signal3Reliability,
    },
    {
      key: 'medianSyncMin',
      label: 'Synchro médiane',
      render: (u) => orNC(u.medianSyncMin, (v) => <span className="text-gray-600 dark:text-gray-300">{fmtMin(v)}</span>),
      rawValue: (u) => u.medianSyncMin,
    },
    {
      key: 'medianCoverage',
      exportLabel: 'Couverture médiane',
      label: (
        <span className="inline-flex items-center gap-1">
          Couverture médiane
          <InfoTip width="w-80" text="Part du temps déclaré que le chronomètre a effectivement mesurée. INDICE DE PRIORISATION, PAS UNE PREUVE : le chronomètre ne mesure que le temps passé dans les blocs du formulaire — ni le merchandising, ni les échanges avec les vendeurs, ni les déplacements en magasin. Un écart important peut être parfaitement légitime. Seul l'écart aux autres intervenants du même projet, sur la même période, est interprétable." />
        </span>
      ),
      render: (u) => orNC(u.medianCoverage, (v) => (
        <span>
          {fmtPct(v)}
          {u.peerMedianCoverage !== null && u.peerMedianCoverage !== undefined && (
            <span className="ml-1 text-xs text-gray-400">(pairs {fmtPct(u.peerMedianCoverage, 0)})</span>
          )}
        </span>
      )),
      rawValue: (u) => u.medianCoverage,
    },
    {
      key: 'pctUnder10',
      label: 'Visites sous 10 %',
      render: (u) => orNC(u.pctUnder10, (v) => `${v.toFixed(1)} %`),
      rawValue: (u) => u.pctUnder10,
    },
    {
      key: 'nbLongDeclaredShortChrono',
      exportLabel: '>=60 min déclarées / <=5 mesurées',
      label: (
        <span className="inline-flex items-center gap-1">
          ≥ 60 min déclarées / ≤ 5 mesurées
          <InfoTip width="w-72" text="Nombre de visites déclarées à une heure ou plus dont le chronomètre n'a mesuré que cinq minutes au maximum. Comme la couverture, c'est un indice à vérifier, pas une preuve." />
        </span>
      ),
      render: (u) => (hasChrono ? u.nbLongDeclaredShortChrono : NC),
      rawValue: (u) => u.nbLongDeclaredShortChrono,
    },
    {
      key: 'medianDeclared',
      label: 'Durée déclarée méd.',
      render: (u) => orNC(u.medianDeclared, (v) => `${Math.round(v)} min`),
      rawValue: (u) => u.medianDeclared,
    },
    {
      key: 'medianChrono',
      label: 'Chrono méd.',
      render: (u) => orNC(u.medianChrono, (v) => `${Math.round(v)} min`),
      rawValue: (u) => u.medianChrono,
    },
  ], [displayName, hasChrono, proofCell]);

  const detailColumns = useMemo(() => [
    { key: 'date', label: 'Date', render: (v) => (v.date ? format(new Date(v.date), 'dd/MM/yyyy HH:mm', { locale: fr }) : '—') },
    { key: 'store', label: 'Magasin', render: (v) => v.store || '—' },
    { key: 'declared', label: 'Déclarée', render: (v) => orNC(v.declared, (x) => `${x} min`), rawValue: (v) => v.declared },
    { key: 'chrono', label: 'Mesurée', render: (v) => orNC(v.chrono, (x) => `${x} min`), rawValue: (v) => v.chrono },
    {
      key: 'gap',
      label: 'Écart',
      render: (v) => orNC(v.gap, (x) => (
        <span className={x > 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500'}>
          {x > 0 ? `+${x}` : x} min
        </span>
      )),
      rawValue: (v) => v.gap,
    },
    {
      key: 'blocMax',
      exportLabel: 'Bloc le plus long',
      label: (
        <span className="inline-flex items-center gap-1">
          Bloc le plus long
          <InfoTip width="w-72" text="Durée du bloc de formulaire le plus long. Quand ce bloc porte à lui seul la quasi-totalité du temps mesuré et que les autres sont à zéro, c'est la signature d'une horloge avancée pendant qu'un bloc était ouvert : le chronomètre a encaissé le saut." />
        </span>
      ),
      render: (v) => orNC(v.blocMax, (x) => `${x} min`),
      rawValue: (v) => v.blocMax,
    },
    { key: 'nbBlocs', label: 'Blocs', render: (v) => orNC(v.nbBlocs), rawValue: (v) => v.nbBlocs },
    { key: 'nbZero', label: 'Blocs à 0', render: (v) => orNC(v.nbZero), rawValue: (v) => v.nbZero },
    { key: 'serverDelayMin', label: 'Délai serveur', render: (v) => orNC(v.serverDelayMin, (x) => (
      <span className={x < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-500'}>{fmtMin(x)}</span>
    )), rawValue: (v) => v.serverDelayMin },
    {
      key: 'signals',
      label: 'À vérifier',
      render: (v) => (v.signals?.length ? (
        <span className="inline-flex flex-wrap gap-1">
          {v.signals.includes('hard-inconsistency') && <Badge tone="violet">Incohérence dure</Badge>}
          {v.signals.includes('impossible-delay') && <Badge tone="violet">Délai impossible</Badge>}
          {v.signals.includes('low-coverage') && <Badge tone="amber">Couverture faible</Badge>}
        </span>
      ) : <span className="text-gray-400">—</span>),
      rawValue: (v) => (v.signals || []).join(' '),
    },
  ], []);

  if (!selectedProject) return null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64" role="status">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Banner tone="amber" title="Les indicateurs de contrôle n'ont pas pu être calculés">
        {error}. Aucun chiffre n'est affiché volontairement : un tableau vide se lirait à tort comme « aucune anomalie ».
      </Banner>
    );
  }

  if (!project || !users.length) {
    return (
      <Banner tone="slate" title="Aucune visite à contrôler sur cette période">
        Le contrôle ne porte que sur les visites réellement effectuées, hors visites supprimées, spéciales et
        déclarées « n'a pas eu lieu ».
      </Banner>
    );
  }

  const availability = (key, label, tip) => {
    const a = AVAILABILITY_LABEL[project[key]] || AVAILABILITY_LABEL.unavailable;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
        <Badge tone={a.tone} dot>{a.text}</Badge>
        <InfoTip width="w-72" text={tip} />
      </span>
    );
  };

  return (
    <div className="space-y-4">
      <StatCard
        title="Contrôle qualité des déclarations"
        subtitle={`${format(startDate, 'dd/MM/yyyy')} – ${format(endDate, 'dd/MM/yyyy')} · ${project.nbVisits} visites contrôlées`}
        icon={(
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        )}
      >
        <p className="text-sm text-gray-600 dark:text-gray-300 text-left mb-3">
          L'application chronomètre elle-même le temps passé dans chaque bloc de formulaire ; l'intervenant ne peut
          pas écrire dans ce chronomètre. La durée de début et de fin, elle, vient de l'horloge du téléphone. Cet
          écran confronte les deux.
        </p>

        <p className="text-xs text-gray-500 dark:text-gray-400 text-left mb-3">
          Seule la période s'applique à cet écran : l'équipe complète du projet est toujours affichée. Les filtres
          effectif et enseigne sont volontairement sans effet ici — sur un contrôle, écarter quelqu'un par mégarde
          serait le pire défaut. La comparaison aux pairs suppose d'ailleurs que tout le monde soit présent.
        </p>

        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
          {availability('declaredDurationAvailability', 'Durée déclarée',
            "Sans durée déclarée, aucun des trois indicateurs ne peut être calculé : il n'y a rien à contredire.")}
          {availability('chronoAvailability', 'Chronomètre (indic. 1 et 2)',
            "Tous les projets n'utilisent pas de chronomètre de formulaire. Sans lui, l'incohérence dure et la couverture sont incalculables — et non pas nulles.")}
          {availability('signal3Availability', 'Délai serveur (indic. 3)',
            "Nécessite une durée déclarée et au moins une réponse horodatée par le serveur.")}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTileNew size="m" label="Visites contrôlées" value={project.nbVisits}
            description="Visites réellement effectuées sur la période, hors supprimées, spéciales et « n'a pas eu lieu »." />
          <StatTileNew size="m" label="Incohérences dures"
            value={totals.signal1 === null ? 'n/c' : totals.signal1}
            description="Preuve arithmétique : le temps mesuré dépasse le temps déclaré. Aucun faux positif possible." />
          <StatTileNew size="m" label="Délais impossibles"
            value={totals.signal3 === null ? 'n/c' : totals.signal3}
            description="Preuve d'horloge décalée : réponses reçues par le serveur avant la fin déclarée de la visite. Aveugle si la synchronisation est tardive." />
          <StatTileNew size="m" label="Intervenants concernés"
            value={totals.signal1 === null ? 'n/c' : totals.flaggedPeople}
            description="Nombre d'intervenants présentant au moins une incohérence dure sur la période." />
        </div>
      </StatCard>

      {project.periodBeforeAnswersCoverage && (
        <Banner title="Période partiellement hors couverture des données">
          Les réponses de formulaire ne sont disponibles qu'à partir du {format(new Date(project.answersCoverageFrom), 'dd/MM/yyyy')}.
          Avant cette date, aucun des trois indicateurs n'est calculable : ce qui s'affiche à « n/c » n'est pas un
          résultat nul, c'est une absence de mesure.
        </Banner>
      )}

      {project.chronoAvailability === 'unavailable' && (
        <Banner tone="slate" title="Ce projet n'utilise pas de chronomètre de formulaire">
          Les indicateurs « Incohérences dures » et « Couverture » ne peuvent pas être calculés ici. Ils s'affichent
          en « n/c » et non à zéro : rien ne permet de conclure à l'absence d'anomalie.
        </Banner>
      )}

      {project.declaredDurationAvailability === 'unavailable' && (
        <Banner tone="slate" title="Aucune durée déclarée sur cette période">
          Les visites de ce projet ne portent pas de durée réelle. Sans durée déclarée il n'y a rien à confronter :
          les trois indicateurs sont incalculables.
        </Banner>
      )}

      {project.signal3Suspect && (
        <Banner title="Indicateur « Délais impossibles » à valider sur ce projet">
          Le pire délai observé ici est de {fmtMin(project.worstDelayMin)}. Un tel écart ne s'explique pas par une
          horloge décalée : il indique très probablement un mode de saisie différent (saisie web ou rétroactive,
          réponses créées au moment de la planification). Les chiffres restent affichés, mais ils ne doivent pas
          être interprétés comme des anomalies d'horloge avant validation du fonctionnement de ce projet.
        </Banner>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
        <div className="flex items-baseline justify-between gap-3 mb-3 text-left">
          <h3 className="text-base font-bold text-gray-900 dark:text-white">Par intervenant</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Colonnes teintées = preuve · colonnes neutres = indices de priorisation. Cliquez une ligne pour ouvrir le détail visite par visite.
          </p>
        </div>
        <SortableTable
          data={users}
          columns={columns}
          onRowClick={openDetail}
          countLabel="intervenants"
          exportable
          exportFilename={`controle-qualite-${selectedProject.value}-${ymd(startDate)}.csv`}
          emptyMessage="Aucun intervenant sur cette période."
        />
      </div>

      <Modal
        isOpen={!!selectedUser}
        onClose={closeDrilldown}
        size="xl"
        fullHeight
        title={selectedUser ? `Contrôle qualité — ${displayName(selectedUser)}` : ''}
      >
        <div className="p-4 flex-1 min-h-0 overflow-y-auto space-y-3">
          {visitContent.visitId ? (
            <>
              <button
                type="button"
                onClick={() => setVisitContent({ visitId: null, loading: false, error: null, data: null })}
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour à la liste des visites
              </button>

              {visitContent.loading && (
                <div className="flex justify-center items-center h-32" role="status">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
                </div>
              )}
              {visitContent.error && (
                <Banner tone="amber" title="Le contenu de la visite n'a pas pu être chargé">{visitContent.error}</Banner>
              )}
              <VisitsQualityVisitContent content={visitContent.data} />
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-left">
                Visites triées par gravité : incohérence dure d'abord, puis délai impossible, puis écart décroissant.
                Cliquez une visite pour lire son contenu, section par section.
              </p>

              {detail.loading && (
                <div className="flex justify-center items-center h-32" role="status">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
                </div>
              )}

              {detail.error && (
                <Banner tone="amber" title="Le détail n'a pas pu être chargé">{detail.error}</Banner>
              )}

              {!detail.loading && !detail.error && (
                <SortableTable
                  data={detail.visits}
                  columns={detailColumns}
                  onRowClick={openVisit}
                  countLabel="visites"
                  exportable
                  exportFilename={`controle-qualite-${selectedProject.value}-${selectedUser?.userId}.csv`}
                  emptyMessage="Aucune visite sur cette période."
                />
              )}
            </>
          )}
        </div>
      </Modal>

      {!hasDelay && !hasChrono && (
        <p className="text-xs text-gray-500 dark:text-gray-400 text-left">
          Aucun indicateur n'est calculable sur ce projet et cette période. Le tableau reste affiché pour montrer la
          population contrôlée, mais il ne porte aucune conclusion.
        </p>
      )}
    </div>
  );
};

export default VisitsQuality;
